import React, { useState, useEffect, useMemo } from 'react';
import { useShare } from '../context/ShareContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const FORMAT_CONFIG = {
  leaderboard: {
    desktop: '728 x 90 px',
    mobile: '320 x 50 px',
    aspectDesktop: 'aspect-[728/90]',
    aspectMobile: 'aspect-[320/50]',
    maxHeightDesktop: 'max-h-[90px]',
    maxHeightMobile: 'max-h-[50px]',
    label: 'Leaderboard'
  },
  rectangle: {
    desktop: '300 x 250 px',
    mobile: '300 x 250 px',
    aspectDesktop: 'aspect-[300/250]',
    aspectMobile: 'aspect-[300/250]',
    maxHeightDesktop: 'max-h-[250px]',
    maxHeightMobile: 'max-h-[250px]',
    label: 'Medium Rectangle'
  },
  billboard: {
    desktop: '970 x 250 px',
    mobile: '320 x 100 px',
    aspectDesktop: 'aspect-[970/250]',
    aspectMobile: 'aspect-[320/100]',
    maxHeightDesktop: 'max-h-[250px]',
    maxHeightMobile: 'max-h-[100px]',
    label: 'Billboard'
  }
};

const AdBlock = ({ slot }) => {
  const { user, adBlocks = [], adImages = [] } = useShare();
  const [randomImage, setRandomImage] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const block = useMemo(() => {
    return adBlocks.find(b => b.slot === slot && b.is_active);
  }, [adBlocks, slot]);

  const images = useMemo(() => {
    if (!block) return [];
    return adImages.filter(img => img.block_id === block.id);
  }, [adImages, block]);

  useEffect(() => {
    if (images.length === 0) {
      setRandomImage(null);
      return;
    }
    const idx = Math.floor(Math.random() * images.length);
    setLoaded(false);
    setRandomImage(images[idx]);
  }, [images]);

  const isAdmin = user?.role === 'admin' || user?.role === 'moderator';
  const config = FORMAT_CONFIG[block?.format] || FORMAT_CONFIG.leaderboard;
  const isRect = block?.format === 'rectangle';
  const aspectClass = isRect
    ? 'aspect-[300/250]'
    : block?.format === 'billboard'
      ? 'aspect-[320/100] sm:aspect-[970/250]'
      : 'aspect-[320/50] sm:aspect-[728/90]';

  // Kein Block für diesen Slot vorhanden
  if (!block) return null;

  // Block existiert, aber keine Bilder -> Platzhalter anzeigen
  if (images.length === 0 || !randomImage) {
    return (
      <div className="my-8 sm:my-10">
        <div className="relative group">
          {/* Ad Label */}
          <div className="absolute -top-5 left-0 z-10">
            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
              <SafeIcon icon={FiIcons.FiTag} className="text-[8px]" />
              Anzeige
            </span>
          </div>

          <div
            className={`
              relative overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50
              flex flex-col items-center justify-center text-center
              transition-all duration-300 hover:border-gray-400 hover:bg-gray-100
              ${aspectClass} min-h-[50px]
            `}
          >
            <SafeIcon icon={FiIcons.FiImage} className="text-gray-300 mb-2" style={{ fontSize: isRect ? '2rem' : '1.25rem' }} />
            <p className="text-gray-500 font-bold text-sm">
              Hier könnte Ihre Werbung stehen
            </p>
            <p className="text-gray-400 text-xs mt-1">
              {config.label} — {config.desktop}
            </p>
            {isAdmin && (
              <p className="text-purple-500 text-[10px] font-bold mt-2 bg-purple-50 px-3 py-1 rounded-full">
                Bilder im Admin-Bereich hochladen
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const content = (
    <div className="relative group">
      {/* Ad Label */}
      <div className="absolute -top-5 left-0 z-10">
        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
          <SafeIcon icon={FiIcons.FiTag} className="text-[8px]" />
          Anzeige
        </span>
      </div>

      {/* Container */}
      <div
        className={`
          relative overflow-hidden rounded-xl bg-gray-100 border border-gray-200
          transition-all duration-300 hover:shadow-lg hover:border-gray-300
          ${isRect ? 'w-full' : 'w-full'}
        `}
      >
        <img
          src={randomImage.image_url}
          alt={randomImage.alt_text || 'Werbung'}
          className={`
            w-full object-cover transition-opacity duration-500
            ${loaded ? 'opacity-100' : 'opacity-0'}
            ${aspectClass}
          `}
          onLoad={() => setLoaded(true)}
        />

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-200 rounded-xl pointer-events-none" />
      </div>
    </div>
  );

  if (block.link_url) {
    return (
      <a
        href={block.link_url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        className="block my-8 sm:my-10"
      >
        {content}
      </a>
    );
  }

  return <div className="my-8 sm:my-10">{content}</div>;
};

export default AdBlock;
